import { IntlMessageFormat } from 'intl-messageformat';

/** Supported locales. Vietnamese is always the default and the source of truth (docs/i18n/glossary.md §1). */
export type Locale = 'vi' | 'en';
export const LOCALES: readonly Locale[] = ['vi', 'en'];
export const DEFAULT_LOCALE: Locale = 'vi';

export type Messages = Readonly<Record<string, string>>;
export type MessageParams = Record<string, string | number | boolean | Date>;
export type TFunction = (key: string, params?: MessageParams) => string;

export interface TranslatorOptions {
  locale: Locale;
  /** Messages per locale. `vi` is required; other locales may be partial or not yet loaded. */
  messages: Partial<Record<Locale, Messages>> & { vi: Messages };
  /** Called once per missing key (dev warning). */
  onMissing?: (key: string, locale: Locale) => void;
}

/**
 * Creates a `t()` that formats ICU messages, falls back to Vietnamese when a key is missing in another locale,
 * and returns the key itself when it is missing everywhere (so gaps are visible, never blank).
 */
export function createTranslator({ locale, messages, onMissing }: TranslatorOptions): TFunction {
  const cache = new Map<string, IntlMessageFormat>();
  const reported = new Set<string>();

  const report = (key: string, loc: Locale) => {
    const id = `${loc}:${key}`;
    if (reported.has(id)) return;
    reported.add(id);
    onMissing?.(key, loc);
  };

  const compile = (key: string, loc: Locale, source: string): IntlMessageFormat => {
    const id = `${loc}:${key}`;
    let fmt = cache.get(id);
    if (!fmt) {
      fmt = new IntlMessageFormat(source, loc);
      cache.set(id, fmt);
    }
    return fmt;
  };

  return (key, params) => {
    let loc = locale;
    let source = messages[locale]?.[key];
    if (source === undefined) {
      if (locale !== DEFAULT_LOCALE) report(key, locale);
      loc = DEFAULT_LOCALE;
      source = messages.vi[key];
    }
    if (source === undefined) {
      report(key, DEFAULT_LOCALE);
      return key;
    }
    const out = compile(key, loc, source).format(params);
    return typeof out === 'string' ? out : String(out);
  };
}

/**
 * Locale resolution: `?lang=` in the URL wins (for shared links), then the saved setting, otherwise Vietnamese.
 * The browser language is deliberately ignored — VI is shown first for everyone.
 */
export function resolveLocale(search: string, saved: string | null | undefined): Locale {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (isLocale(fromUrl)) return fromUrl;
  if (isLocale(saved)) return saved;
  return DEFAULT_LOCALE;
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}
